import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import confetti from "canvas-confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CartContext = createContext(null);

const STORAGE_KEY = "house_rules_cart_v1";

// ------ Neon confetti + Web Audio click sound (fires on every Add-to-Cart) ------
const NEON_COLORS = ["#FF2C8C", "#7B2CFF", "#296CFF", "#FF7A00", "#F8F8FA", "#A742FF"];
let _audioCtx = null;
function fireConfetti() {
  if (typeof window === "undefined") return;
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  const defaults = { colors: NEON_COLORS, ticks: 200, gravity: 0.9, scalar: 1.1, disableForReducedMotion: true };
  confetti({ ...defaults, particleCount: 60, spread: 65, startVelocity: 45, origin: { y: 0.75 } });
  setTimeout(() => confetti({ ...defaults, particleCount: 40, angle: 60, spread: 55, origin: { x: 0, y: 0.8 } }), 120);
  setTimeout(() => confetti({ ...defaults, particleCount: 40, angle: 120, spread: 55, origin: { x: 1, y: 0.8 } }), 220);
}
function clickSound() {
  try {
    if (typeof window === "undefined") return;
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(1760, ctx.currentTime + 0.09);
    g.gain.setValueAtTime(0.12, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.18);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.2);
  } catch (e) { /* ignore */ }
}
function celebrate() { clickSound(); fireConfetti(); }

export function CartProvider({ children }) {
  // Initialize synchronously from localStorage to avoid overwriting on first render
  const [items, setItems] = useState(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });
  const [products, setProducts] = useState({}); // {id: product}
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  // Persist whenever items change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // ignore
    }
  }, [items]);

  // Load products from backend
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/products`);
        if (!mounted) return;
        const map = {};
        res.data.forEach((p) => (map[p.id] = p));
        setProducts(map);
      } catch (e) {
        console.error("Failed to load products", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const addItem = useCallback((productId, qty = 1) => {
    setItems((prev) => {
      const existing = prev.find((i) => i.product_id === productId);
      if (existing) {
        return prev.map((i) =>
          i.product_id === productId ? { ...i, quantity: i.quantity + qty } : i
        );
      }
      return [...prev, { product_id: productId, quantity: qty }];
    });
    setOpen(true);
    toast.success("Added to cart");
    celebrate();
  }, []);

  const removeItem = useCallback((productId) => {
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
  }, []);

  const updateQty = useCallback((productId, qty) => {
    if (qty <= 0) {
      setItems((prev) => prev.filter((i) => i.product_id !== productId));
      return;
    }
    setItems((prev) =>
      prev.map((i) => (i.product_id === productId ? { ...i, quantity: qty } : i))
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const detailedItems = useMemo(() => {
    return items
      .map((i) => {
        const p = products[i.product_id];
        if (!p) return null;
        return { ...i, product: p, lineTotal: p.price * i.quantity };
      })
      .filter(Boolean);
  }, [items, products]);

  const subtotal = useMemo(
    () => detailedItems.reduce((sum, i) => sum + i.lineTotal, 0),
    [detailedItems]
  );

  const count = useMemo(
    () => items.reduce((sum, i) => sum + i.quantity, 0),
    [items]
  );

  const productsList = useMemo(
    () => Object.values(products).sort((a, b) => (a.sort || 0) - (b.sort || 0)),
    [products]
  );

  const startCheckout = useCallback(
    async ({ buyNow } = {}) => {
      const cartItems = buyNow ? [buyNow] : items;
      if (!cartItems.length) {
        toast.error("Your cart is empty");
        return;
      }
      try {
        toast.loading("Preparing your checkout…", { id: "ck" });
        const res = await axios.post(`${API}/checkout/session`, {
          items: cartItems,
          origin_url: window.location.origin,
        });
        toast.dismiss("ck");
        if (res.data?.url) {
          window.location.href = res.data.url;
        } else {
          toast.error("Could not start checkout. Please try again.");
        }
      } catch (err) {
        toast.dismiss("ck");
        console.error("Checkout failed", err);
        toast.error(err?.response?.data?.detail || "Checkout failed. Try again.");
      }
    },
    [items]
  );

  const value = {
    items,
    detailedItems,
    products,
    productsList,
    subtotal,
    count,
    loading,
    open,
    setOpen,
    addItem,
    removeItem,
    updateQty,
    clear,
    startCheckout,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
